import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import type { StageId } from '../data/content';
import { LAYERS, LAYER_GAP, STACK_BASE } from '../lib/layout';
import { useLabelFactor } from './sceneHooks';
type Props = {
  width: number;
  stage: StageId;
  reducedMotion: boolean;
};
const SLAB_COLOR = new THREE.Color('#A9B2E0');
const EDGE_COLOR = new THREE.Color('#8A93C8');
const {
  damp,
  clamp
} = THREE.MathUtils;
export function LayerStack({
  width,
  stage,
  reducedMotion
}: Props) {
  const visible = stage === 'layers' || stage === 'prediction';
  const labelFactor = useLabelFactor();
  const rootRef = useRef<THREE.Group>(null);
  const slabRefs = useRef<(THREE.Group | null)[]>([]);
  const slabMatRefs = useRef<(THREE.MeshStandardMaterial | null)[]>([]);
  const edgeMatRefs = useRef<(THREE.LineBasicMaterial | null)[]>([]);
  const progress = useRef(0);
  const slabW = width + 2.2;
  const geometry = useMemo(() => new THREE.BoxGeometry(slabW, 0.05, 2.6), [slabW]);
  const edges = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry]);
  useEffect(() => () => {
    geometry.dispose();
    edges.dispose();
  }, [geometry, edges]);
  useFrame((state, delta) => {
    const d = Math.min(delta, 0.05);
    progress.current = damp(progress.current, visible ? 1 : 0, 3, d);
    const root = rootRef.current;
    if (!root) return;
    root.visible = progress.current > 0.005;
    const t = state.clock.elapsedTime;
    for (let l = 0; l < LAYERS; l++) {
      const p = clamp(progress.current * 1.8 - l * 0.12, 0, 1);
      const slab = slabRefs.current[l];
      if (slab) slab.position.y = STACK_BASE + l * LAYER_GAP - (1 - p) * 0.6;
      const wave = stage === 'layers' && !reducedMotion ? Math.max(0, Math.sin(t * 2.2 - l * 0.9)) : 0.3;
      const mat = slabMatRefs.current[l];
      if (mat) mat.opacity = p * (0.05 + 0.08 * wave);
      const edge = edgeMatRefs.current[l];
      if (edge) edge.opacity = p * (0.3 + 0.4 * wave);
    }
  });
  return <group ref={rootRef} visible={false} data-id="element-36" data-mp="23js3ulyznm91">
      {Array.from({
      length: LAYERS
    }, (_, l) => <group key={l} ref={el => {
      slabRefs.current[l] = el;
    }} position={[0, STACK_BASE + l * LAYER_GAP, 0]} data-id="element-37" data-mp="28iv8w6eyxsh0">
          <mesh geometry={geometry} raycast={() => null} data-id="element-38" data-mp="2dg4wrhmplw2q">
            <meshStandardMaterial ref={el => {
          slabMatRefs.current[l] = el;
        }} color={SLAB_COLOR} emissive={SLAB_COLOR} emissiveIntensity={0.25} transparent opacity={0} depthWrite={false} data-id="element-39" data-mp="2igswqssx1n93" />
          </mesh>
          <lineSegments geometry={edges} raycast={() => null} data-id="element-40" data-mp="31827t6ic4k2s">
            <lineBasicMaterial ref={el => {
          edgeMatRefs.current[l] = el;
        }} color={EDGE_COLOR} transparent opacity={0} data-id="element-41" data-mp="319mygl1j3ivd" />
          </lineSegments>
          {stage === 'layers' && <Html position={[-slabW / 2 - 0.2, 0, 1.3]} distanceFactor={10 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-42" data-mp="pjxbdihh2opk">
              <div className="-translate-x-full -translate-y-1/2 select-none whitespace-nowrap text-right animate-[fadeIn_0.8s_ease_both]" data-id="element-43" data-mp="1da1e7nmu6kfi">
                <div className="font-mono text-[14px] text-[#9A9FB0]" data-id="element-44" data-mp="18cj6g0kk2b93">Layer {l + 1}</div>
                {l === 0 && <div className="text-[12px] text-[#5D6272]" data-id="element-45" data-mp="13iev7549bh27">attention + feed-forward</div>}
              </div>
            </Html>}
        </group>)}
    </group>;
}